import { useState, useEffect, useCallback } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { getReport, saveProjection, saveActual } from "../../services/reportService";
import { errorToast, successToast } from "../Toasts/Toasts";

// The projection, and what actually happened.
//
// Every figure is editable here on purpose: a forecast that needs a deploy
// to revise stops being revised, and then stops being believed.
//
// The two halves are not symmetrical and the page does not pretend they are.
// B2B actuals come from Zoho Books on every load. B2C actuals are typed in,
// because this app has no Shopify connection - so a B2C month that nobody
// entered shows as absent, not as zero sales. Those are very different
// things and drawing them the same way would be a lie.
//
// Cost lines have no actual column at all. There is no expense data in the
// app, and an empty column invites somebody to fill it with a guess.

const METRICS = [
  { key: "orders", label: "Orders / month", kind: "number" },
  { key: "aov", label: "Average order value", kind: "money" },
  { key: "revenue", label: "Revenue / month", kind: "money" },
  { key: "grossMarginPct", label: "Gross margin", kind: "pct" },
  { key: "marketingPct", label: "Marketing", kind: "pct", planOnly: true },
  { key: "opsPct", label: "Operations + delivery", kind: "pct", planOnly: true },
  { key: "teamPct", label: "Team + overhead", kind: "pct", planOnly: true },
];

const inr = (n) =>
  n === null || n === undefined ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

// Lakhs read faster than seven digits when the point is the shape of a plan.
const lakh = (n) => (n === null || n === undefined ? "—" : Number(n) >= 100000 ? `₹${(Number(n) / 100000).toFixed(2)}L` : inr(n));

function show(metric, kind) {
  if (!metric) return "—";
  const { low, high } = metric;
  const one = (v) => (kind === "money" ? inr(v) : kind === "pct" ? `${v}%` : Number(v).toLocaleString("en-IN"));
  return low === high ? one(low) : `${kind === "pct" ? low : one(low)}–${one(high)}`;
}

function Sparkline({ months, valueOf }) {
  const pts = months.filter((m) => valueOf(m) !== null && valueOf(m) !== undefined);
  if (pts.length < 2) return null;
  const vals = pts.map(valueOf).map(Number);
  const max = Math.max(...vals);
  const min = Math.min(...vals, 0);
  const w = 260;
  const h = 40;
  const x = (i) => (i / (pts.length - 1)) * w;
  const y = (v) => h - ((v - min) / (max - min || 1)) * h;
  const d = vals.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} className="d-block" role="img" aria-label="monthly trend">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.7" />
      <circle cx={x(pts.length - 1)} cy={y(vals[vals.length - 1])} r="2.5" fill="currentColor" />
    </svg>
  );
}

const Projections = () => {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null); // { segment, horizon, label, metrics }
  const [entry, setEntry] = useState(null); // { segment, month, orders, gross, net }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await getReport();
      setData(r.data || r);
    } catch (e) {
      errorToast("Could not load the report");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const commitProjection = async () => {
    setBusy(true);
    try {
      await saveProjection(edit.segment, edit.horizon, { metrics: edit.metrics });
      successToast(`${edit.label} updated`);
      setEdit(null);
      load();
    } catch (e) {
      errorToast(e?.response?.data?.message || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const commitActual = async () => {
    setBusy(true);
    try {
      await saveActual(entry.segment, entry.month, { orders: entry.orders, gross: entry.gross, net: entry.net });
      successToast(`${entry.month} saved`);
      setEntry(null);
      load();
    } catch (e) {
      errorToast(e?.response?.data?.message || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;

  const flagged = (data.reconciliation || []).filter((r) => r.checked && !r.agrees);

  const Segment = ({ segment, title, note }) => {
    const s = data.segments[segment];
    const plan = s.plan;
    const t = s.trailing12;
    const disagrees = (horizon) => flagged.find((f) => f.segment === segment && f.horizon === horizon);

    return (
      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h6 className="fw-bold mb-0">{title}</h6>
              <div className="small text-muted">{note}</div>
            </div>
            {t && (
              <div className="text-end small">
                <div className="fw-semibold">Last 12 months</div>
                <div className="text-muted">
                  {lakh(t.grossPerMonth)}/mo · {t.ordersPerMonth} orders/mo · AOV {inr(t.aov)}
                </div>
                <div className="text-muted">{t.from} to {t.to}, {t.monthsWithData} month(s) with data</div>
              </div>
            )}
          </div>

          <div className="table-responsive table-scroll mt-2">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th style={{ minWidth: 170 }}>Metric</th>
                  {plan.map((p) => (
                    <th key={p.horizon} className="text-end">
                      {p.label}
                      {disagrees(p.horizon) && (
                        <span
                          className="badge bg-warning text-dark ms-1"
                          title={`orders × AOV is ${inr(disagrees(p.horizon).derived)}, but revenue says ${inr(disagrees(p.horizon).stated)}`}
                        >
                          ?
                        </span>
                      )}
                    </th>
                  ))}
                  <th className="text-end" style={{ minWidth: 110 }}>Actual</th>
                </tr>
              </thead>
              <tbody>
                {METRICS.map((m) => (
                  <tr key={m.key}>
                    <td className="small">
                      {m.label}
                      {m.planOnly && <span className="text-muted"> · plan only</span>}
                    </td>
                    {plan.map((p) => (
                      <td key={p.horizon} className="text-end small">
                        <button
                          type="button"
                          className="btn btn-link p-0 text-reset text-decoration-none"
                          title="Edit"
                          onClick={() => setEdit({ segment, horizon: p.horizon, label: p.label, metrics: { ...p.metrics } })}
                        >
                          {show(p.metrics[m.key], m.kind)}
                        </button>
                      </td>
                    ))}
                    <td className="text-end small fw-semibold">
                      {!t || m.planOnly ? (
                        <span className="text-muted">—</span>
                      ) : m.key === "orders" ? (
                        t.ordersPerMonth
                      ) : m.key === "aov" ? (
                        inr(t.aov)
                      ) : m.key === "revenue" ? (
                        lakh(t.grossPerMonth)
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-end flex-wrap gap-3 mt-3">
            <div className="text-success">
              <Sparkline months={s.actuals.months} valueOf={(m) => m.gross} />
              <div className="small text-muted">
                {s.actuals.available
                  ? `${s.actuals.months.length} month(s) · ${s.actuals.manual ? "entered by hand" : "from Zoho Books"}`
                  : s.actuals.reason}
              </div>
            </div>
            {/* Only a hand-entered segment gets this. B2B actuals are read
                from Books and that table is never consulted for them, so a
                B2B month typed here would be saved and then never shown -
                which is worse than not offering it. */}
            {s.actuals.manual === true && (
              <Button size="sm" variant="outline-success" onClick={() => setEntry({ segment, month: "", orders: "", gross: "", net: "" })}>
                Enter a month
              </Button>
            )}
          </div>

          {s.actuals.months.length > 0 && (
            <div className="table-responsive table-scroll mt-2" style={{ maxHeight: "34vh" }}>
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th className="text-end">Orders</th>
                    <th className="text-end">Gross</th>
                    <th className="text-end">Net</th>
                    <th className="text-end">AOV</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {[...s.actuals.months].reverse().map((m) => (
                    <tr key={m.month}>
                      <td className="small">{m.month}</td>
                      <td className="text-end small">{m.orders ?? "—"}</td>
                      <td className="text-end small">{inr(m.gross)}</td>
                      <td className="text-end small">{s.actuals.netIsGross ? <span className="text-muted">same</span> : inr(m.net)}</td>
                      <td className="text-end small">{m.orders ? inr(Number(m.gross) / m.orders) : "—"}</td>
                      <td className="text-end">
                        {s.actuals.manual && (
                          <Button size="sm" variant="link" className="p-0 small" onClick={() => setEntry({ segment, month: m.month, orders: m.orders ?? "", gross: m.gross ?? "", net: m.net ?? "" })}>
                            edit
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {s.actuals.netIsGross && (
            <div className="small text-muted mt-1">{s.actuals.note}</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="page-body px-0 py-lg-2 py-1 mt-0 mt-lg-3 ms-3">
      <div className="container-fluid p-0">
        <div className="row-title px-2">
          <h6 className="fw-bold mb-0">Financial projections</h6>
          <div className="small text-muted">
            The plan beside what actually happened. Click any projection figure to change it.
          </div>
        </div>

        {flagged.length > 0 && (
          <div className="alert alert-warning py-2 px-3 mt-2 small">
            <div className="fw-semibold">
              {flagged.length === 1 ? "One column does not" : `${flagged.length} columns do not`} agree with their own inputs
            </div>
            {flagged.map((f) => (
              <div key={`${f.segment}-${f.horizon}`}>
                {f.segment.toUpperCase()} {f.label}: orders × AOV comes to <b>{inr(f.derived)}</b>, but revenue says{" "}
                <b>{inr(f.stated)}</b> ({f.gapPct > 0 ? "+" : ""}{f.gapPct}%)
              </div>
            ))}
          </div>
        )}

        <div className="mt-2">
          <Segment
            segment="b2c"
            title="B2C"
            note="Retail orders. Actuals are entered by hand — there is no Shopify connection in this app yet."
          />
          <Segment
            segment="b2b"
            title="B2B"
            note="Societies and bulk. Actuals are read from Zoho Books invoices every time this page loads."
          />
        </div>

        <div className="small text-muted px-2">
          Marketing, operations and team are targets with no actual beside them: this app holds no expense data, and a
          column that can only be guessed at is worse than no column.
        </div>
      </div>

      <Modal show={!!edit} onHide={() => setEdit(null)}>
        <Modal.Header closeButton>
          <Modal.Title className="h6">{edit && `${edit.segment.toUpperCase()} · ${edit.label}`}</Modal.Title>
        </Modal.Header>
        {edit && (
          <Modal.Body>
            <div className="small text-muted mb-2">
              Leave the second box empty for an exact figure; fill both for a range like 20–25%.
            </div>
            {METRICS.map((m) => {
              const v = edit.metrics[m.key] || { low: "", high: "" };
              const set = (part, value) =>
                setEdit({ ...edit, metrics: { ...edit.metrics, [m.key]: { ...v, [part]: value } } });
              return (
                <div key={m.key} className="d-flex align-items-center gap-2 mb-2">
                  <label className="small flex-grow-1">{m.label}</label>
                  <Form.Control size="sm" style={{ width: 110 }} value={v.low ?? ""} onChange={(e) => set("low", e.target.value)} />
                  <span className="text-muted">–</span>
                  <Form.Control size="sm" style={{ width: 110 }} value={v.high ?? ""} onChange={(e) => set("high", e.target.value)} />
                </div>
              );
            })}
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="success" disabled={busy} onClick={commitProjection}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!entry} onHide={() => setEntry(null)}>
        <Modal.Header closeButton>
          <Modal.Title className="h6">{entry && `${entry.segment.toUpperCase()} · a month's actuals`}</Modal.Title>
        </Modal.Header>
        {entry && (
          <Modal.Body>
            <Form.Group className="mb-2">
              <Form.Label className="small">Month</Form.Label>
              <Form.Control size="sm" placeholder="2026-09" value={entry.month} onChange={(e) => setEntry({ ...entry, month: e.target.value })} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label className="small">Orders</Form.Label>
              <Form.Control size="sm" value={entry.orders} onChange={(e) => setEntry({ ...entry, orders: e.target.value })} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label className="small">Gross sales</Form.Label>
              <Form.Control size="sm" value={entry.gross} onChange={(e) => setEntry({ ...entry, gross: e.target.value })} />
            </Form.Group>
            <Form.Group>
              <Form.Label className="small">Net sales</Form.Label>
              <Form.Control size="sm" value={entry.net} onChange={(e) => setEntry({ ...entry, net: e.target.value })} />
              <Form.Text className="text-muted">
                Shopify reports both, and they differ by about a quarter. Entering the same number twice hides that.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="success" disabled={busy || !entry?.month} onClick={commitActual}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Projections;
